import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const BackArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
);
const Star = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="#ffb400" stroke="none"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"/></svg>
);

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bgOk, setBgOk] = useState(true);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/app");
    } catch (err: any) {
      setError(err?.message ?? "Sign in failed. Check your email and password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper-card">
      {/* ── Left: image panel (hidden on small screens) ── */}
      <div className="relative hidden lg:block bg-ink overflow-hidden">
        {bgOk && (
          <img src="/cars/login-bg.png" alt="" onError={() => setBgOk(false)}
            className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/30" />
        {/* brand */}
        <Link to="/" className="relative z-10 flex items-center gap-2.5 p-8">
          <span className="h-8 w-8 rounded-[10px] bg-white grid place-items-center"><span className="h-3 w-3 rounded-[3px] bg-accent" /></span>
          <span className="text-white font-extrabold text-xl tracking-tight">FleetLease</span>
        </Link>
        {/* tagline */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3.5 py-1.5 mb-5">
            <Star /><span className="text-white text-[13px] font-bold">Rated 4.96 · 1,200+ drivers</span>
          </div>
          <h2 className="text-white text-4xl font-extrabold tracking-[-0.035em] leading-[1.05] max-w-md">A better car,<br/>by the month.</h2>
          <p className="text-white/70 mt-3 max-w-sm">Manage your fleet, clients, rentals and payments — all in one place.</p>
        </div>
      </div>

      {/* ── Right: form panel ── */}
      <div className="relative flex flex-col px-6 py-8 sm:px-10">
        <div className="flex items-center justify-between">
          {/* logo (shown on mobile where the left panel is hidden) */}
          <Link to="/" className="flex items-center gap-2.5 lg:invisible">
            <span className="h-[30px] w-[30px] rounded-[9px] bg-ink grid place-items-center"><span className="h-[11px] w-[11px] rounded-[3px] bg-accent" /></span>
            <span className="font-extrabold text-lg tracking-tight">FleetLease</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-ink-muted hover:text-ink text-sm font-semibold">
            <BackArrow /> Back to home
          </Link>
        </div>

        <div className="flex-1 grid place-items-center">
          <form onSubmit={onSubmit} className="w-full max-w-sm">
            <h1 className="text-3xl font-extrabold tracking-tight">Welcome back</h1>
            <p className="text-ink-muted mt-1.5 mb-7">Sign in to your FleetLease account.</p>

            <label className="block text-sm font-semibold mb-1.5" htmlFor="email">Email</label>
            <input id="email" type="email" autoComplete="username" value={email}
              onChange={(e) => setEmail(e.target.value)} required
              className="w-full rounded-xl border border-black/10 px-3.5 py-3 mb-4 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15" />
            <label className="block text-sm font-semibold mb-1.5" htmlFor="pw">Password</label>
            <input id="pw" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} required
              className="w-full rounded-xl border border-black/10 px-3.5 py-3 mb-5 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15" />
            {error && <div className="mb-4 rounded-xl bg-danger/10 px-3.5 py-2.5 text-sm text-danger">{error}</div>}
            <button type="submit" disabled={busy}
              className="w-full rounded-xl bg-ink px-4 py-3.5 font-bold text-white hover:bg-black disabled:opacity-60 transition-colors">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <p className="mt-6 text-center text-sm text-ink-muted">
              Browsing for a car? <Link to="/" className="font-semibold text-accent hover:underline">Back to landing page</Link>
            </p>
          </form>
        </div>

        <div className="text-center text-xs text-ink-muted/70">© {new Date().getFullYear()} FleetLease · AccTech AI</div>
      </div>
    </div>
  );
}
